import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, TablePagination } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getEmployeeTimesheet } from "api/clockInWorkforce";
import moment from "moment";
import { addDays } from "date-fns";
import TimesheetDetailModal from "./TimesheetDetailModal";
import HistoryTimelineModal from "./HistoryTimelineModal";
import Button from "components/common/Button";
import PageLoader from "components/common/PageLoader";
import CustomChip from "components/common/Chip";
import DateRangeSelect from "components/common/DateRangeSelect";
import Daterange from "components/common/Daterange";
import "./TimesheetTab.scss";

function CustomerTimesheetView({ customerId, employeeId, lastAction }) {
  const [timesheetData, setTimesheetData] = useState([]);
  const [stats, setStats] = useState(null);
  const [currentShift, setCurrentShift] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [dateRange, setDateRange] = useState({
    startDate: addDays(new Date(), -30),
    endDate: new Date(),
  });

  useEffect(() => {
    if (customerId && employeeId) {
      fetchTimesheet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, employeeId, page, rowsPerPage, dateRange]);

  const fetchTimesheet = async () => {
    try {
      setLoading(true);
      const response = await getEmployeeTimesheet(customerId, employeeId, {
        startDate: moment(dateRange.startDate).format("YYYY-MM-DD"),
        endDate: moment(dateRange.endDate).format("YYYY-MM-DD"),
        page: page + 1,
        limit: rowsPerPage,
      });

      if (response?.success && response?.data) {
        const { rows, stats: responseStats, currentShift: responseCurrentShift, pagination } = response.data;
        setTimesheetData(rows || []);
        setStats(responseStats || null);
        setCurrentShift(responseCurrentShift || null);
        setTotalRecords(pagination?.totalRecords || rows?.length || 0);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching timesheet:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (time) => {
    return time ? moment(time).format("hh:mm A") : "-";
  };

  const isNextDay = (clockInTime, clockOutTime) => {
    if (!clockInTime || !clockOutTime) return false;
    const inDate = moment(clockInTime).format("YYYY-MM-DD");
    const outDate = moment(clockOutTime).format("YYYY-MM-DD");
    return inDate !== outDate;
  };

  const handleRowClick = (row) => {
    const canViewDetails =
      row.clockIn ||
      row.inTime ||
      row.status?.toLowerCase().includes("worked") ||
      row.status?.toLowerCase().includes("present");

    if (canViewDetails) {
      setSelectedRow(row);
      setShowDetailModal(true);
    }
  };

  const handleViewHistory = () => {
    setShowHistoryModal(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDateRangeChange = (selection) => {
    setDateRange({
      startDate: selection.startDate,
      endDate: selection.endDate,
    });
    setPage(0);
  };

  return (
    <Box className="customer-timesheet-container">
      <Box className="timesheet-header">
        <p className="heading-h4-semibold">Timesheet</p>
      </Box>

      <Box className="timesheet-controls">
        <Box className="date-range-picker-wrapper">
          <DateRangeSelect
            name="dateRange"
            label="Date Range"
            value={dateRange}
            onChange={handleDateRangeChange}
            dateRangeComponent={<Daterange months={2} disabledFutureDates />}
          />
        </Box>

        <Box className="clock-in-history-section">
          <p className="para-body-m-regular text-headings-secondary">
            {lastAction
              ? `Attendance ${lastAction.action} on ${moment(lastAction.actionAt).format("MMM DD, YYYY")}`
              : "Attendance tracking is enabled"}
          </p>
          <Button label="View history" variant="text" color="secondary" size="small" onClick={handleViewHistory} />
        </Box>
      </Box>

      <Box className="timesheet-summary-cards">
        <Box className="summary-card">
          <p className="card-label para-body-m-regular">Current shift time (local time)</p>
          <p className="card-value heading-h3-semibold">{currentShift?.shiftTime || "Not recorded"}</p>
        </Box>
        <Box className="summary-card">
          <p className="card-label para-body-m-regular">Total missed shifts</p>
          <p className="card-value heading-h3-semibold">{stats?.absentDays !== undefined ? stats.absentDays : 0}</p>
        </Box>
        <Box className="summary-card">
          <p className="card-label para-body-m-regular">Total overtime</p>
          <p className="card-value heading-h3-semibold">{stats?.totalOvertime || "0"}</p>
        </Box>
      </Box>

      <Box className="timesheet-table-wrapper">
        {loading && <PageLoader />}
        {!loading && timesheetData.length > 0 && (
          <table className="timesheet-table">
            <thead>
              <tr>
                <th className="heading-h5-medium">Date</th>
                <th className="heading-h5-medium">Status</th>
                <th className="heading-h5-medium">Clocked in</th>
                <th className="heading-h5-medium">Clocked out</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {timesheetData.map((row) => {
                const clockInTime = row.clockIn || row.inTime;
                const clockOutTime = row.clockOut || row.outTime;
                const showNextDayBadge = isNextDay(clockInTime, clockOutTime);
                const hasDetails = clockInTime || row.status?.toLowerCase().includes("worked");
                const isMissed = row.status?.toLowerCase().includes("missed");
                const isWeekend = row.status?.toLowerCase().includes("weekend");
                const isHoliday = row.status?.toLowerCase().includes("holiday");
                const isLeave = row.status?.toLowerCase().includes("leave");

                // Namespaced class names — a bare `holiday-row` here picked up an
                // unrelated global `display: flex` rule and broke the row's layout.
                let rowClass = "";
                if (isMissed) rowClass = "timesheet-row-missed";
                else if (isWeekend) rowClass = "timesheet-row-weekend";
                else if (isHoliday) rowClass = "timesheet-row-holiday";
                else if (isLeave) rowClass = "timesheet-row-leave";

                return (
                  <tr key={row.timesheetId || `timesheet-${row.date}`} className={rowClass}>
                    <td className="para-body-m-regular">{moment(row.date).format("MMM D, YYYY")}</td>
                    <td>
                      <CustomChip
                        label={row.status || "-"}
                        size="small"
                        variant="filled"
                        color={isMissed ? "error" : "default"}
                        labelClass="para-body-s-medium"
                        startIcon={isMissed ? <InfoOutlinedIcon sx={{ fontSize: 16 }} /> : null}
                        className={isMissed ? "status-chip-missed" : "status-chip"}
                      />
                    </td>
                    <td className="para-body-m-regular">{formatTime(clockInTime)}</td>
                    <td className="para-body-m-regular">
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {formatTime(clockOutTime)}
                        {showNextDayBadge && clockOutTime !== "-" && <span className="next-day-badge">Next day</span>}
                      </Box>
                    </td>
                    <td className="timesheet-row-action">
                      {hasDetails && (
                        <IconButton size="small" onClick={() => handleRowClick(row)}>
                          <ChevronRightIcon />
                        </IconButton>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {!loading && timesheetData.length === 0 && (
          <Box className="timesheet-empty-message">
            <p className="para-body-m-regular">No timesheet data available for the selected period</p>
          </Box>
        )}
      </Box>

      {!loading && timesheetData.length > 0 && (
        <Box className="timesheet-pagination">
          <TablePagination
            component="div"
            count={totalRecords}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
          />
        </Box>
      )}

      {showDetailModal && selectedRow && (
        <TimesheetDetailModal
          open={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          timesheetData={selectedRow}
        />
      )}

      {showHistoryModal && (
        <HistoryTimelineModal
          open={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          customerId={customerId}
          employeeId={employeeId}
        />
      )}
    </Box>
  );
}

CustomerTimesheetView.propTypes = {
  customerId: PropTypes.string.isRequired,
  employeeId: PropTypes.string.isRequired,
  lastAction: PropTypes.shape({
    action: PropTypes.string,
    actionAt: PropTypes.string,
  }),
};

export default CustomerTimesheetView;
